import { useState, useEffect, useCallback, useRef } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  mutationFunctions as chldMutationFunctions,
  enrlMutationFunctions,
  clenMutationFunctions,
  langMutationFunctions,
  slngMutationFunctions,
  attnMutationFunctions,
  prgmMutationFunctions,
  CHLD,
} from "@/models/chld";
import { mutationFunctions as famiMutationFunctions } from "@/models/fami";
import {
  MutationResponse,
  ENRL,
  CLEN,
  LANG,
  SLNG,
  ATTN,
  PRGM,
  LeaDataModel,
} from "@/models";
import { FAMI } from "@/models/fami";
import {
  FileSubmission,
  FileStatusEnum,
  ValidationError,
} from "@/models/file-submission";
import { useFileSubmissionsData, useFileSubmissionErrors } from "@/queries";

type SkipFlags = {
  skipFamily: boolean;
  skipLanguage: boolean;
  skipSupplementalLanguage: boolean;
  skipEnrollment: boolean;
  skipClassroomEnrollment: boolean;
  showSecondFamily?: boolean;
  activeClassroomCount: number;
  skipAttendance: boolean;
  activeAttendanceCount: number;
  skipPrgm: boolean;
  activePrgmCount: number;
};

type Forms = {
  chldForm: UseFormReturn<CHLD>;
  familyForm: UseFormReturn<FAMI>;
  familyForm2?: UseFormReturn<FAMI>;
  languageForm: UseFormReturn<LANG>;
  supplementalLanguageForm: UseFormReturn<SLNG>;
  enrollmentForm: UseFormReturn<ENRL>;
  classroomEnrollmentForms: UseFormReturn<CLEN>[];
  attendanceForms: UseFormReturn<ATTN>[];
  prgmForms: UseFormReturn<PRGM>[];
};

type SubmissionResult = {
  chld: MutationResponse<CHLD> | null;
  fami: MutationResponse<FAMI> | null;
  fami2: MutationResponse<FAMI> | null;
  lang: MutationResponse<LANG> | null;
  slng: MutationResponse<SLNG> | null;
  enrl: MutationResponse<ENRL> | null;
  clens: Array<MutationResponse<CLEN> | null>;
  attns: Array<MutationResponse<ATTN> | null>;
  prgms: Array<MutationResponse<PRGM> | null>;
};

type SubmissionCallbacks = {
  onSubmitting?: () => void;
  onChldPosted?: () => void;
  onComplete?: () => void;
  onChldFailed?: (errors: ValidationError<keyof CHLD>[]) => void;
  addPendingFileSubmission?: (fs: FileSubmission) => void;
};

type SubmissionPhase =
  | "idle"
  | "submitting-chld"
  | "waiting-for-chld"
  | "submitting-related"
  | "complete";

export function useChildSubmission(
  forms: Forms,
  skipFlags: SkipFlags,
  leaData: LeaDataModel,
  esiIdPath: string,
  callbacks?: SubmissionCallbacks
) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phase, setPhase] = useState<SubmissionPhase>("idle");
  const [chldFileSubmissionId, setChldFileSubmissionId] = useState<
    string | null
  >(null);

  // Track failed submission ID to fetch detailed errors
  const [failedSubmissionId, setFailedSubmissionId] = useState<string | null>(
    null
  );

  // Track which file submissions have been added to prevent duplicates
  const addedSubmissionIds = useRef<Set<string | number>>(new Set());

  const [results, setResults] = useState<SubmissionResult>({
    chld: null,
    fami: null,
    fami2: null,
    lang: null,
    slng: null,
    enrl: null,
    clens: Array(5).fill(null),
    attns: Array.from({ length: 12 }, () => null),
    prgms: Array(5).fill(null),
  });

  // Result setters
  const setChldResult = (r: MutationResponse<CHLD>) => {
    setResults((prev) => ({ ...prev, chld: r }));
  };

  const setFamiResult = (r: MutationResponse<FAMI>) => {
    setResults((prev) => ({ ...prev, fami: r }));
  };

  const setFami2Result = (r: MutationResponse<FAMI>) => {
    setResults((prev) => ({ ...prev, fami2: r }));
  };

  const setLangResult = (r: MutationResponse<LANG>) => {
    setResults((prev) => ({ ...prev, lang: r }));
  };

  const setSlngResult = (r: MutationResponse<SLNG>) => {
    setResults((prev) => ({ ...prev, slng: r }));
  };

  const setEnrlResult = (r: MutationResponse<ENRL>) => {
    setResults((prev) => ({ ...prev, enrl: r }));
  };

  const setClenResultAt = (index: number) => (r: MutationResponse<CLEN>) => {
    setResults((prev) => {
      const next = [...prev.clens];
      next[index] = r;
      return { ...prev, clens: next };
    });
  };

  const setAttnResultAt = (index: number) => (r: MutationResponse<ATTN>) => {
    setResults((prev) => {
      const next = [...prev.attns];
      next[index] = r;
      return { ...prev, attns: next };
    });
  };

  const setPrgmResultAt = (index: number) => (r: MutationResponse<PRGM>) => {
    setResults((prev) => {
      const next = [...prev.prgms];
      next[index] = r;
      return { ...prev, prgms: next };
    });
  };

  const chldMutateFns = chldMutationFunctions(leaData, setChldResult);
  const famiMutateFns = famiMutationFunctions(leaData, setFamiResult);
  const fami2MutateFns = famiMutationFunctions(leaData, setFami2Result);
  const langMutateFns = langMutationFunctions(leaData, setLangResult);
  const slngMutateFns = slngMutationFunctions(leaData, setSlngResult);
  const enrlMutateFns = enrlMutationFunctions(leaData, setEnrlResult);

  const clenMutateFnsArray = [
    clenMutationFunctions(leaData, setClenResultAt(0)),
    clenMutationFunctions(leaData, setClenResultAt(1)),
    clenMutationFunctions(leaData, setClenResultAt(2)),
    clenMutationFunctions(leaData, setClenResultAt(3)),
    clenMutationFunctions(leaData, setClenResultAt(4)),
  ];

  const attnMutateFnsArray = [
    attnMutationFunctions(leaData, setAttnResultAt(0)),
    attnMutationFunctions(leaData, setAttnResultAt(1)),
    attnMutationFunctions(leaData, setAttnResultAt(2)),
    attnMutationFunctions(leaData, setAttnResultAt(3)),
    attnMutationFunctions(leaData, setAttnResultAt(4)),
    attnMutationFunctions(leaData, setAttnResultAt(5)),
    attnMutationFunctions(leaData, setAttnResultAt(6)),
    attnMutationFunctions(leaData, setAttnResultAt(7)),
    attnMutationFunctions(leaData, setAttnResultAt(8)),
    attnMutationFunctions(leaData, setAttnResultAt(9)),
    attnMutationFunctions(leaData, setAttnResultAt(10)),
    attnMutationFunctions(leaData, setAttnResultAt(11)),
  ];

  const prgmMutateFnsArray = [
    prgmMutationFunctions(leaData, setPrgmResultAt(0)),
    prgmMutationFunctions(leaData, setPrgmResultAt(1)),
    prgmMutationFunctions(leaData, setPrgmResultAt(2)),
    prgmMutationFunctions(leaData, setPrgmResultAt(3)),
    prgmMutationFunctions(leaData, setPrgmResultAt(4)),
  ];

  const refreshInterval = phase === "waiting-for-chld" ? 3000 : 3600000;
  const submissionDataQuery = useFileSubmissionsData(
    esiIdPath,
    refreshInterval
  );

  // Fetch detailed errors when a submission fails
  const errorsQuery = useFileSubmissionErrors(
    esiIdPath,
    failedSubmissionId || "",
    !!failedSubmissionId
  );

  // Helper to add file submission only once
  const addSubmissionOnce = useCallback(
    (fs: FileSubmission | null | undefined) => {
      if (
        fs &&
        callbacks?.addPendingFileSubmission &&
        !addedSubmissionIds.current.has(fs.Id)
      ) {
        addedSubmissionIds.current.add(fs.Id);
        callbacks.addPendingFileSubmission(fs);
      }
    },
    [callbacks]
  );

  // Submit related records after CHLD is posted
  // The backend will look up the CSPPID from LocalChildId since CHLD is now posted
  const submitRelatedRecords = useCallback(async () => {
    setPhase("submitting-related");

    const localChildId = forms.chldForm.getValues().LocalChildId || "";

    const famiData = forms.familyForm.getValues();
    famiData.LocalChildId = localChildId;

    const fami2Data = forms.familyForm2?.getValues();
    if (fami2Data) fami2Data.LocalChildId = localChildId;

    const langData = forms.languageForm.getValues();
    langData.LocalChildId = localChildId;

    const slngData = forms.supplementalLanguageForm.getValues();
    slngData.LocalChildId = localChildId;

    const enrlData = forms.enrollmentForm.getValues();
    enrlData.LocalChildId = localChildId;

    const clenDataList = forms.classroomEnrollmentForms
      .slice(0, skipFlags.activeClassroomCount)
      .map((f) => {
        const data = f.getValues();
        data.LocalChildId = localChildId;
        return data;
      });

    const attnDataList = forms.attendanceForms
      .slice(0, skipFlags.activeAttendanceCount)
      .map((f) => {
        const data = f.getValues();
        data.LocalChildId = localChildId;
        return data;
      });

    const prgmDataList = forms.prgmForms
      .slice(0, skipFlags.activePrgmCount)
      .map((f) => {
        const data = f.getValues();
        data.LocalChildId = localChildId;
        return data;
      });

    // FAMI
    if (!skipFlags.skipFamily) {
      await famiMutateFns.handleSave(famiData);
    }

    // FAMI 2
    if (!skipFlags.skipFamily && skipFlags.showSecondFamily && fami2Data) {
      await fami2MutateFns.handleSave(fami2Data);
    }

    // LANG
    if (!skipFlags.skipLanguage) {
      await langMutateFns.handleSave(langData);
    }

    // SLNG
    if (!skipFlags.skipSupplementalLanguage) {
      await slngMutateFns.handleSave(slngData);
    }

    // ENRL
    if (!skipFlags.skipEnrollment) {
      await enrlMutateFns.handleSave(enrlData);
    }

    // CLEN(s)
    if (!skipFlags.skipClassroomEnrollment) {
      for (const [i, clenData] of clenDataList.entries()) {
        await clenMutateFnsArray[i].handleSave(clenData);
      }
    }

    if (!skipFlags.skipAttendance) {
      for (const [i, attnData] of attnDataList.entries()) {
        await attnMutateFnsArray[i].handleSave(attnData, false, "A");
      }
    }

    if (!skipFlags.skipPrgm) {
      for (const [i, prgmData] of prgmDataList.entries()) {
        await prgmMutateFnsArray[i].handleSave(prgmData, false, "A");
      }
    }

    setPhase("complete");
    setIsSubmitting(false);

    if (callbacks?.onComplete) {
      callbacks.onComplete();
    }
  }, [
    forms,
    skipFlags,
    famiMutateFns,
    fami2MutateFns,
    langMutateFns,
    slngMutateFns,
    enrlMutateFns,
    clenMutateFnsArray,
    attnMutateFnsArray,
    prgmMutateFnsArray,
    callbacks,
  ]);

  // Watch for CHLD mutation result - start polling when we get the file submission ID
  useEffect(() => {
    if (!results.chld || phase !== "submitting-chld") return;

    if (results.chld.fileSubmission) {
      // CHLD submitted to backend, now poll for Posted status
      setChldFileSubmissionId(results.chld.fileSubmission.Id);
      setPhase("waiting-for-chld");
    }
  }, [results.chld, phase]);

  // Poll for CHLD status - check if Posted or Failed
  useEffect(() => {
    if (phase !== "waiting-for-chld" || !chldFileSubmissionId) return;
    if (!submissionDataQuery.data) return;

    const chldSubmission = submissionDataQuery.data.find(
      (x) => x.Id === chldFileSubmissionId
    );

    if (!chldSubmission) return;

    if (chldSubmission.CurrentStatus === FileStatusEnum.Posted) {
      // CHLD posted successfully - now submit related records
      // The backend will look up CSPPID from LocalChildId since CHLD is now posted
      addSubmissionOnce(chldSubmission);

      // Notify that CHLD posted - wizard can close now
      if (callbacks?.onChldPosted) {
        callbacks.onChldPosted();
      }

      // Submit related records in the background
      submitRelatedRecords();
    } else if (
      chldSubmission.CurrentStatus === FileStatusEnum.Failed ||
      chldSubmission.CurrentStatus === FileStatusEnum.Rejected
    ) {
      // CHLD failed - keep wizard open so user can fix errors
      // Add to tracker so global toast shows
      addSubmissionOnce(chldSubmission);

      console.log("=== CHLD FAILED ===");
      console.log("Submission ID:", chldSubmission.Id);

      // Set the failed submission ID to trigger the errors query
      setFailedSubmissionId(String(chldSubmission.Id));

      setPhase("idle");
      setIsSubmitting(false);
      setChldFileSubmissionId(null);
    }
  }, [
    phase,
    chldFileSubmissionId,
    submissionDataQuery.data,
    addSubmissionOnce,
    submitRelatedRecords,
    forms.chldForm,
    callbacks,
  ]);

  // Process detailed errors when they are fetched
  useEffect(() => {
    if (!failedSubmissionId || !errorsQuery.data) return;

    const errors = errorsQuery.data;
    console.log("=== DETAILED ERRORS FETCHED ===");
    console.log("Errors:", errors);

    // Set form errors
    if (errors.length > 0) {
      errors.forEach((e) => {
        forms.chldForm.setError((e.Fieldname || "root") as any, {
          type: "manual",
          message: e.Message || "Validation error",
        });
      });
    }

    // Call onChldFailed callback with the actual errors
    if (callbacks?.onChldFailed) {
      if (errors.length > 0) {
        callbacks.onChldFailed(errors as any);
      } else {
        callbacks.onChldFailed([
          {
            Message:
              "CHLD validation failed. Please check the View Submissions page for details.",
          },
        ] as any);
      }
    }

    // Clear the failed submission ID
    setFailedSubmissionId(null);
  }, [failedSubmissionId, errorsQuery.data, forms.chldForm, callbacks]);

  // Watch for related record results - add to pending submissions
  useEffect(() => {
    if (phase !== "submitting-related" && phase !== "complete") return;

    const resultMappings = [
      { result: results.fami, form: forms.familyForm },
      { result: results.fami2, form: forms.familyForm2 },
      { result: results.lang, form: forms.languageForm },
      { result: results.slng, form: forms.supplementalLanguageForm },
      { result: results.enrl, form: forms.enrollmentForm },
      ...results.clens.map((result, i) => ({
        result,
        form: forms.classroomEnrollmentForms[i],
      })),
      ...results.attns.map((result, i) => ({
        result,
        form: forms.attendanceForms[i],
      })),
      ...results.prgms.map((result, i) => ({
        result,
        form: forms.prgmForms[i],
      })),
    ];

    resultMappings.forEach(({ result, form }) => {
      if (!form) return;
      if (result?.errors?.length) {
        result.errors.forEach((e: any) => {
          form.setError(e.Fieldname || e.fieldname, {
            type: "manual",
            message: e.Message || e.message,
          });
        });
      }
      addSubmissionOnce(result?.fileSubmission);
    });
  }, [
    phase,
    results.fami,
    results.fami2,
    results.lang,
    results.slng,
    results.enrl,
    results.clens,
    results.attns,
    results.prgms,
    forms,
    addSubmissionOnce,
  ]);

  // Main submit function
  const submitChild = useCallback(
    async (isNewRecord: boolean) => {
      setIsSubmitting(true);
      setPhase("submitting-chld");
      setChldFileSubmissionId(null);
      setFailedSubmissionId(null);

      // Clear tracked submission IDs for new submission
      addedSubmissionIds.current.clear();

      // Reset all results
      setResults({
        chld: null,
        fami: null,
        fami2: null,
        lang: null,
        slng: null,
        enrl: null,
        clens: Array(5).fill(null),
        attns: Array.from({ length: 12 }, () => null),
        prgms: Array(5).fill(null),
      });

      // Validate CHLD form
      const isChildValid = await forms.chldForm.trigger();

      if (!isChildValid) {
        setIsSubmitting(false);
        setPhase("idle");
        return;
      }

      // Get CHLD form data
      const childData = forms.chldForm.getValues();

      // Handle CSPPID for new records - delete so backend can auto-generate
      if (isNewRecord) {
        delete (childData as any).CSPPID;
      }

      // Call onSubmitting callback for processing toast
      if (callbacks?.onSubmitting) {
        callbacks.onSubmitting();
      }

      // Submit CHLD first
      try {
        await chldMutateFns.handleSave(childData);
      } catch (error) {
        console.error("Error submitting CHLD:", error);
        setIsSubmitting(false);
        setPhase("idle");
      }
    },
    [forms.chldForm, chldMutateFns, callbacks]
  );

  return {
    submitChild,
    isSubmitting,
    phase,
    results,
  };
}
